import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@courier/shared';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ShieldCheck, Building2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ConnectingDotsMap, RoadFooter } from './LoginBackdrop';
import { LoginCaptcha, type CaptchaValue } from './LoginCaptcha';

interface LoginPageProps {
  portal: 'admin' | 'merchant';
  mode?: 'login' | 'signup';
}

export function LoginPage({ portal, mode = 'login' }: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    document.body.classList.add('login-page');
    return () => document.body.classList.remove('login-page');
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaValue>({ captchaToken: '', captchaAnswer: '' });
  /* Bumped after ANY failed sign-in: the code on screen has been spent server-side, so leaving
     it there would make the next attempt fail even when the password is right. */
  const [captchaKey, setCaptchaKey] = useState(0);
  const { loginAdmin, loginMerchant } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setSubmitting(true);
    let shopifyInstall = false;
    try {
      if (portal === 'admin') {
        await loginAdmin(data.email, data.password, captcha);
      } else {
        await loginMerchant(data.email, data.password, captcha);
        shopifyInstall = (useAuth.getState().user as any)?.shopifyAppInstall === true;
      }
      toast.success('Welcome back!');
      // A Shopify App Store account is shown no payment surface anywhere in the product, so it must
      // not be DROPPED on one at login either — the sidebar hid the wallet while this line sent them
      // straight to it. Everyone else still lands on the wallet, which is what they asked for.
      const dest = portal === 'merchant' ? (shopifyInstall ? '/dashboard' : '/wallet') : '/dashboard';
      setTimeout(() => { window.location.href = dest; }, 200);
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Login failed';
      toast.error(msg);
      // Whatever went wrong — wrong password or wrong code — the challenge is spent. Fetch a new one.
      setCaptchaKey((k) => k + 1);
      setSubmitting(false);
    }
  };

  const brand = portal === 'admin'
    ? {
        title: 'Admin Portal', subtitle: 'Super Admin + Internal Users', icon: <ShieldCheck size={28} />,
        headline: 'Every parcel,\non one map.',
        blurb: 'Orders, tracking, NDR, invoices and COD remittance for B2B and B2C — one control room for the whole network.',
      }
    : {
        title: 'Merchant Portal', subtitle: 'Manage your shipments', icon: <Building2 size={28} />,
        headline: 'Your shipments,\nmoving as you sleep.',
        blurb: 'Book, track and settle from one place — live status, NDR actions, invoices and COD remittance for every order you send.',
      };

  // Facts about the network, not invented numbers: 5 hubs are drawn on the map, and the platform
  // genuinely runs both services with a public tracking page.
  const marks = [
    { k: 'Pan-India', v: 'Pickup & delivery' },
    { k: 'B2C + B2B', v: 'One platform' },
    { k: 'Live', v: 'Tracking & NDR' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left: the network. The map sits BEHIND the copy at low opacity so the text stays the
          thing you read first — decoration must not compete with the words next to the form. */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary to-primary-900 text-white">
        <ConnectingDotsMap className="absolute inset-0 w-full h-full opacity-[0.55]" />
        {/* Darkened towards the bottom-left, where the copy sits. */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/85 via-primary-900/25 to-transparent" />

        <div className="relative flex flex-col justify-between p-12 w-full">
          <div>
            <div className="font-bold text-2xl tracking-tight">Gaint Logistic</div>
            <div className="text-primary-200 text-sm mt-1">Courier Platform</div>
          </div>

          <div>
            <h2 className="text-4xl font-bold leading-tight whitespace-pre-line">{brand.headline}</h2>
            <p className="text-primary-100/80 mt-4 max-w-md">{brand.blurb}</p>

            <div className="flex gap-8 mt-8">
              {marks.map((m) => (
                <div key={m.k}>
                  <div className="text-lg font-semibold">{m.k}</div>
                  <div className="text-primary-200/80 text-xs mt-0.5">{m.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-primary-300/80 text-sm">© 2026 Gaint Logistic Private Limited</div>
        </div>

        {/* The truck runs along the bottom of the artwork on desktop. */}
        <RoadFooter className="absolute bottom-0 left-0 right-0 text-white/70" />
      </div>

      <div className="flex-1 flex flex-col bg-slate-50 relative">
        <div className="flex-1 flex items-center justify-center p-4 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-primary text-white p-3 rounded-xl">{brand.icon}</div>
            <div>
              <div className="font-bold text-2xl text-slate-900">{brand.title}</div>
              <div className="text-slate-500 text-sm">{brand.subtitle}</div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-xl font-semibold text-slate-900">
              {mode === 'signup' ? 'Create your account' : 'Welcome back'}
            </h3>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {portal === 'admin' ? 'Email or EMP Code' : 'Email'}
              </label>
              <input type="text" autoComplete="username" {...register('email')}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder={portal === 'admin' ? 'you@company.com  OR  GLP900022' : 'you@company.com'} />
              {portal === 'admin' && (
                <p className="text-xs text-slate-400 mt-1">Internal users can sign in with their employee code instead of email.</p>
              )}
              {errors.email && <p className="text-danger text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" {...register('password')}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none pr-10"
                  placeholder="********" />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-danger text-xs mt-1">{errors.password.message}</p>}
            </div>

            {mode !== 'signup' && (
              <LoginCaptcha value={captcha} onChange={setCaptcha} reloadKey={captchaKey} />
            )}

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-600">
                <input type="checkbox" {...register('rememberMe')} className="rounded" />
                Remember me
              </label>
              <a href="/forgot-password" className="text-primary hover:underline">Forgot password?</a>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {mode === 'signup' ? 'Create account' : 'Sign in'}
            </button>

            {portal === 'merchant' && (
              <p className="text-center text-sm text-slate-600 pt-2 border-t border-slate-100">
                New here?{' '}
                <a href={mode === 'login' ? '/signup' : '/login'} className="text-primary hover:underline font-medium">
                  {mode === 'login' ? 'Create an account' : 'Sign in'}
                </a>
              </p>
            )}
          </form>
        </div>
        </div>

        {/* Same truck under the form. On phones and tablets the left panel is hidden entirely, so
            without this the page would lose the artwork exactly where there is most room for it. */}
        <RoadFooter className="text-slate-300 shrink-0" />
      </div>
    </div>
  );
}
